import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Layout from '../components/Layout';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography component="h2" sx={{ fontWeight: 800, color: '#0F1117', fontSize: { xs: '1.1rem', md: '1.25rem' }, mb: 1.5, letterSpacing: '-0.01em' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography sx={{ color: '#374151', lineHeight: 1.85, fontSize: '0.95rem', mb: 1.5 }}>{children}</Typography>
);

const Li = ({ children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366F1', mt: '8px', flexShrink: 0 }} />
    <Typography sx={{ color: '#374151', lineHeight: 1.8, fontSize: '0.95rem' }}>{children}</Typography>
  </Box>
);

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | InfyniHub';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'InfyniHub Privacy Policy — how we collect, use, and protect your personal information.');
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', 'Privacy Policy | InfyniHub');
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', 'https://infynihub.com/privacy-policy');
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', 'https://infynihub.com/privacy-policy');
  }, []);

  return (
    <Layout>
      <Box sx={{ bgcolor: '#FFFFFF', pt: { xs: '140px', md: '150px' } }}>

        {/* Header */}
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#F8F9FF', borderBottom: '1px solid #F3F4F6' }}>
          <Container maxWidth="md" sx={{ px: { xs: 3, md: 4 } }}>
            <Button onClick={() => navigate('/')} startIcon={<ArrowBackIcon />}
              sx={{ color: '#6B7280', mb: 2, textTransform: 'none', fontWeight: 600, px: 0, '&:hover': { color: '#6366F1', bgcolor: 'transparent' } }}>
              Back to Home
            </Button>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '100px', px: 2, py: 0.6, mb: 3 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366F1' }} />
              <Typography sx={{ color: '#4F46E5', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Legal</Typography>
            </Box>
            <Typography component="h1" sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '2rem', md: '2.8rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', mb: 2 }}>
              Privacy Policy
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '0.9rem' }}>
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>
          </Container>
        </Box>

        {/* Content */}
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <Container maxWidth="md" sx={{ px: { xs: 3, md: 4 } }}>

            <P>
              InfyniHub ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website at infynihub.com or use our products, GearDesk and LineDesk.
            </P>
            <P>
              Please read this policy carefully. If you disagree with its terms, please discontinue use of our site and services.
            </P>

            <Section title="1. Information We Collect">
              <P>We may collect the following types of information:</P>
              <Li><strong>Contact information</strong> — name, email address, phone number, and company name when you submit an enquiry or contact form.</Li>
              <Li><strong>Usage data</strong> — pages visited, time spent on pages, browser type, and referring URLs collected automatically via analytics tools.</Li>
              <Li><strong>Communications</strong> — messages you send us via email or contact forms.</Li>
              <Li><strong>Product data</strong> — if you use GearDesk or LineDesk, data you enter into those platforms as part of normal use.</Li>
            </Section>

            <Section title="2. How We Use Your Information">
              <P>We use the information we collect to:</P>
              <Li>Respond to your enquiries and provide customer support.</Li>
              <Li>Send you information about our products and services that you have requested.</Li>
              <Li>Improve our website, products, and services.</Li>
              <Li>Analyse usage patterns to understand how visitors interact with our site.</Li>
              <Li>Comply with legal obligations.</Li>
            </Section>

            <Section title="3. Sharing Your Information">
              <P>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</P>
              <Li><strong>Service providers</strong> — trusted third-party vendors who assist us in operating our website and services (e.g. email delivery, analytics), bound by confidentiality obligations.</Li>
              <Li><strong>Legal requirements</strong> — if required by law, court order, or governmental authority.</Li>
              <Li><strong>Business transfers</strong> — in connection with a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</Li>
            </Section>

            <Section title="4. Cookies">
              <P>
                Our website may use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small files stored on your device that help us understand how you use our site.
              </P>
              <P>
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some features of our website may not function properly without cookies.
              </P>
            </Section>

            <Section title="5. Third-Party Services">
              <P>Our website may use third-party services including:</P>
              <Li><strong>EmailJS</strong> — for processing contact form submissions.</Li>
              <Li><strong>Netlify</strong> — for website hosting and deployment.</Li>
              <Li><strong>Google Fonts</strong> — for typography.</Li>
              <P>These services have their own privacy policies governing the use of your information.</P>
            </Section>

            <Section title="6. Data Retention">
              <P>
                We retain your personal information only for as long as necessary to fulfil the purposes outlined in this policy, or as required by law. Contact form submissions are retained for a reasonable period to allow us to respond and follow up.
              </P>
            </Section>

            <Section title="7. Data Security">
              <P>
                We implement reasonable technical and organisational measures to protect your personal information from unauthorised access, disclosure, alteration, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </P>
            </Section>

            <Section title="8. Your Rights">
              <P>Depending on your location, you may have the following rights regarding your personal data:</P>
              <Li>The right to access the personal information we hold about you.</Li>
              <Li>The right to request correction of inaccurate information.</Li>
              <Li>The right to request deletion of your personal information.</Li>
              <Li>The right to withdraw consent where processing is based on consent.</Li>
              <P>To exercise any of these rights, please contact us at <Box component="a" href="mailto:admin@infynihub.com" sx={{ color: '#6366F1', textDecoration: 'underline' }}>admin@infynihub.com</Box>.</P>
            </Section>

            <Section title="9. Children's Privacy">
              <P>
                Our website and services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately.
              </P>
            </Section>

            <Section title="10. Changes to This Policy">
              <P>
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by updating the "Last updated" date at the top of this page. Your continued use of our website after any changes constitutes your acceptance of the updated policy.
              </P>
            </Section>

            <Section title="11. Contact Us">
              <P>If you have any questions or concerns about this Privacy Policy, please contact us:</P>
              <Li><strong>Email:</strong> <Box component="a" href="mailto:admin@infynihub.com" sx={{ color: '#6366F1', textDecoration: 'underline' }}>admin@infynihub.com</Box></Li>
              <Li><strong>Website:</strong> <Box component="a" href="https://infynihub.com" sx={{ color: '#6366F1', textDecoration: 'underline' }}>infynihub.com</Box></Li>
            </Section>

          </Container>
        </Box>
      </Box>
    </Layout>
  );
};

export default PrivacyPolicyPage;
